import { useState, useCallback, useRef, useEffect } from 'react'
import {
  Room,
  RoomEvent,
  ConnectionState,
  ConnectionQuality,
  Track,
  RemoteTrack,
  RemoteTrackPublication,
  RemoteParticipant,
  Participant,
  DisconnectReason,
} from 'livekit-client'
import type { Session } from '@supabase/supabase-js'
import { supabase } from '@/lib/supabase'
import type { NetworkState } from '@/types/network'
import { setSessionContext } from '@/lib/sentry'

// Module-level to survive StrictMode remounts
let globalConnectingPromise: Promise<void> | null = null

interface UseLiveKitOptions {
  // Auth session from AuthContext - avoids race condition with duplicate getSession() calls
  session: Session | null
  onConnected?: () => void
  onDisconnected?: () => void
  onRoomDeleted?: () => void // Called when room is deleted (agent ended call)
  onError?: (error: Error) => void
  // Network quality callbacks
  onLocalQualityChanged?: (
    quality: ConnectionQuality,
    previous: ConnectionQuality
  ) => void
  onAgentQualityChanged?: (
    quality: ConnectionQuality | null,
    previous: ConnectionQuality | null
  ) => void
  onReconnecting?: () => void
  onReconnected?: () => void
  onAgentConnected?: () => void
  onAgentDisconnected?: () => void
}

interface ConnectOptions {
  lessonType?: string
  unitId?: string
}

interface UseLiveKitReturn {
  room: Room | null
  sessionId: string | null
  connectionState: ConnectionState
  isConnecting: boolean
  isConnected: boolean
  isWaitingForAgent: boolean
  agentTimedOut: boolean
  error: Error | null
  isMuted: boolean
  networkState: NetworkState
  connect: (options?: ConnectOptions) => Promise<void>
  disconnect: () => Promise<void>
  toggleMute: () => void
  retryAgentConnection: () => Promise<void>
}

// Agent timeout - generous for cold start on free tier
const AGENT_TIMEOUT_MS = 30000

export function useLiveKit(options: UseLiveKitOptions): UseLiveKitReturn {
  const [room, setRoom] = useState<Room | null>(null)
  const [sessionId, setSessionId] = useState<string | null>(null)
  const [connectionState, setConnectionState] = useState<ConnectionState>(
    ConnectionState.Disconnected
  )
  const [isConnecting, setIsConnecting] = useState(false)
  const [error, setError] = useState<Error | null>(null)
  const [isMuted, setIsMuted] = useState(false)
  const [networkState, setNetworkState] = useState<NetworkState>({
    localQuality: ConnectionQuality.Unknown,
    agentQuality: null,
    isAgentConnected: false,
    isReconnecting: false,
  })

  // Agent timeout state
  const [isWaitingForAgent, setIsWaitingForAgent] = useState(false)
  const [agentTimedOut, setAgentTimedOut] = useState(false)
  const agentTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Track agent connection state to trigger re-renders
  const [isAgentConnected, setIsAgentConnected] = useState(false)

  // Refs for StrictMode-safe connection tracking
  const roomRef = useRef<Room | null>(null)
  const isConnectingRef = useRef(false)
  const isMountedRef = useRef(true)
  const audioElementRef = useRef<HTMLAudioElement | null>(null)
  const agentParticipantRef = useRef<RemoteParticipant | null>(null)
  const intentionalDisconnectRef = useRef(false)
  const lastConnectOptionsRef = useRef<ConnectOptions | undefined>(undefined)

  const handleTrackSubscribed = useCallback(
    (
      track: RemoteTrack,
      _publication: RemoteTrackPublication,
      _participant: RemoteParticipant
    ) => {
      console.log('Track subscribed:', {
        kind: track.kind,
        source: track.source,
        participant: _participant.identity,
      })

      if (track.kind === Track.Kind.Audio) {
        console.log('Attaching audio track to element...')
        // Create audio element for remote audio
        if (!audioElementRef.current) {
          audioElementRef.current = document.createElement('audio')
          audioElementRef.current.autoplay = true
          document.body.appendChild(audioElementRef.current)
        }
        track.attach(audioElementRef.current)
        console.log('Audio element state:', {
          paused: audioElementRef.current?.paused,
          muted: audioElementRef.current?.muted,
          readyState: audioElementRef.current?.readyState,
        })
      }
    },
    []
  )

  const handleTrackUnsubscribed = useCallback(
    (track: RemoteTrack) => {
      if (track.kind === Track.Kind.Audio && audioElementRef.current) {
        track.detach(audioElementRef.current)
      }
    },
    []
  )

  const handleConnectionQualityChanged = useCallback(
    (quality: ConnectionQuality, participant: Participant) => {
      if (!isMountedRef.current) return

      const isLocal = participant === roomRef.current?.localParticipant

      setNetworkState((prev) => {
        if (isLocal) {
          if (prev.localQuality !== quality) {
            options.onLocalQualityChanged?.(quality, prev.localQuality)
          }
          return { ...prev, localQuality: quality }
        } else {
          // Remote participant (agent)
          if (prev.agentQuality !== quality) {
            options.onAgentQualityChanged?.(quality, prev.agentQuality)
          }
          return { ...prev, agentQuality: quality }
        }
      })
    },
    [options]
  )

  const handleReconnecting = useCallback(() => {
    if (!isMountedRef.current) return
    setNetworkState((prev) => ({ ...prev, isReconnecting: true }))
    options.onReconnecting?.()
  }, [options])

  const handleReconnected = useCallback(() => {
    if (!isMountedRef.current) return
    setNetworkState((prev) => ({ ...prev, isReconnecting: false }))
    options.onReconnected?.()
  }, [options])

  const handleParticipantConnected = useCallback(
    (participant: RemoteParticipant) => {
      console.log('Participant joined:', participant.identity)
      agentParticipantRef.current = participant
      setIsAgentConnected(true)
      // Agent joined - clear timeout and waiting state
      if (agentTimeoutRef.current) {
        clearTimeout(agentTimeoutRef.current)
        agentTimeoutRef.current = null
      }
      setIsWaitingForAgent(false)
      setAgentTimedOut(false)
      setNetworkState((prev) => ({
        ...prev,
        agentQuality: participant.connectionQuality,
      }))
      options.onAgentConnected?.()
    },
    [options]
  )

  const handleParticipantDisconnected = useCallback(
    (participant: RemoteParticipant) => {
      console.log('Participant left:', participant.identity)
      if (agentParticipantRef.current?.sid === participant.sid) {
        agentParticipantRef.current = null
        setIsAgentConnected(false)
        setNetworkState((prev) => ({
          ...prev,
          agentQuality: null,
        }))
        options.onAgentDisconnected?.()
      }
    },
    [options]
  )

  const connect = useCallback(async (connectOptions?: ConnectOptions) => {
    // If connection already in progress globally, wait for it
    if (globalConnectingPromise) {
      console.log('connect() - awaiting existing connection')
      await globalConnectingPromise
      return
    }

    // Already connected
    if (roomRef.current) {
      console.log('connect() skipped - already connected')
      return
    }

    // Store options for potential retry
    lastConnectOptionsRef.current = connectOptions

    const connectionPromise = (async () => {
      isConnectingRef.current = true
      setIsConnecting(true)
      setError(null)
      // Reset agent timeout state on new connection
      setAgentTimedOut(false)
      setIsWaitingForAgent(false)

      try {
        // Use session from AuthContext (passed via options)
        // This avoids race condition with duplicate getSession() calls
        if (!options.session) {
          throw new Error('Not authenticated')
        }
        console.log('Using auth session from context')

        // Build request body for edge function
        const requestBody: { lesson_type?: string; unit_id?: string } = {}
        if (connectOptions?.lessonType) {
          requestBody.lesson_type = connectOptions.lessonType
        }
        if (connectOptions?.unitId) {
          requestBody.unit_id = connectOptions.unitId
        }

        // Call edge function to get LiveKit token
        console.log('Fetching LiveKit token...', requestBody)
        const { data, error: fnError } = await supabase.functions.invoke('livekit-token', {
          body: requestBody,
        })

        if (fnError || !data) {
          throw new Error(fnError?.message || 'Failed to get LiveKit token')
        }

        const { token, url, sessionId: newSessionId } = data
        console.log('LiveKit token received, connecting to room...')
        setSessionId(newSessionId)
        setSessionContext(newSessionId)

        // Create and connect to room
        const newRoom = new Room({
          adaptiveStream: true,
          dynacast: true,
          disconnectOnPageLeave: true,
          stopLocalTrackOnUnpublish: true,
        })

        // Set up event handlers
        newRoom.on(RoomEvent.TrackSubscribed, handleTrackSubscribed)
        newRoom.on(RoomEvent.TrackUnsubscribed, handleTrackUnsubscribed)
        newRoom.on(RoomEvent.ConnectionStateChanged, (state) => {
          console.log('Connection state changed:', state)
          if (isMountedRef.current) {
            setConnectionState(state)
          }
          if (state === ConnectionState.Connected) {
            options.onConnected?.()
          } else if (state === ConnectionState.Disconnected) {
            options.onDisconnected?.()
          }
        })
        newRoom.on(RoomEvent.Disconnected, (reason?: DisconnectReason) => {
          console.log('Room disconnected, reason:', reason, 'intentional:', intentionalDisconnectRef.current)

          // Skip processing if this was an intentional user disconnect
          // The disconnect() function already handles cleanup
          if (intentionalDisconnectRef.current) {
            console.log('Skipping disconnect handler - intentional disconnect')
            return
          }

          // Clear refs so new session can connect
          roomRef.current = null
          if (isMountedRef.current) {
            setRoom(null)
            setSessionId(null)
          }
          // Clean up audio element
          if (audioElementRef.current) {
            audioElementRef.current.remove()
            audioElementRef.current = null
          }
          // Check if room was deleted (agent ended call)
          if (reason === DisconnectReason.ROOM_DELETED) {
            options.onRoomDeleted?.()
          }
          options.onDisconnected?.()
        })
        newRoom.on(RoomEvent.ParticipantConnected, handleParticipantConnected)
        newRoom.on(RoomEvent.ParticipantDisconnected, handleParticipantDisconnected)
        newRoom.on(RoomEvent.ConnectionQualityChanged, handleConnectionQualityChanged)
        newRoom.on(RoomEvent.Reconnecting, handleReconnecting)
        newRoom.on(RoomEvent.Reconnected, handleReconnected)

        // Connect to room
        await newRoom.connect(url, token)
        console.log('Connected to room')

        // Check for existing participants (agent may already be in room)
        const existingParticipants = Array.from(newRoom.remoteParticipants.values())
        if (existingParticipants.length > 0) {
          const agent = existingParticipants[0]
          console.log('Agent already in room:', agent.identity)
          agentParticipantRef.current = agent
          setIsAgentConnected(true)
          setNetworkState((prev) => ({
            ...prev,
            agentQuality: agent.connectionQuality,
          }))
          options.onAgentConnected?.()
        } else {
          // No agent yet - start timeout and show waiting state
          console.log('Agent not in room yet, starting timeout...')
          setIsWaitingForAgent(true)
          setAgentTimedOut(false)
          agentTimeoutRef.current = setTimeout(() => {
            console.log('Agent connection timeout')
            if (isMountedRef.current) {
              setAgentTimedOut(true)
              setIsWaitingForAgent(false)
            }
          }, AGENT_TIMEOUT_MS)
        }

        // Start audio to handle browser autoplay policy
        await newRoom.startAudio()
        console.log('Audio started - canPlaybackAudio:', newRoom.canPlaybackAudio)

        // Enable microphone with audio processing to prevent echo/feedback
        // Echo cancellation filters out speaker audio from mic input
        // This prevents VAD from detecting agent speech as user input
        await newRoom.localParticipant.setMicrophoneEnabled(true, {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        })
        console.log('Microphone enabled with echo cancellation')

        // Store in ref and state
        roomRef.current = newRoom
        if (isMountedRef.current) {
          setRoom(newRoom)
          setIsConnecting(false)
        }
      } catch (err) {
        console.error('Connection error:', err)
        const error = err instanceof Error ? err : new Error('Connection failed')
        if (isMountedRef.current) {
          setError(error)
          setIsConnecting(false)
        }
        options.onError?.(error)
      } finally {
        isConnectingRef.current = false
        globalConnectingPromise = null
      }
    })()

    globalConnectingPromise = connectionPromise
    await connectionPromise
  }, [
    handleTrackSubscribed,
    handleTrackUnsubscribed,
    handleParticipantConnected,
    handleParticipantDisconnected,
    handleConnectionQualityChanged,
    handleReconnecting,
    handleReconnected,
    options,
  ])

  const disconnect = useCallback(async () => {
    // Clear agent timeout
    if (agentTimeoutRef.current) {
      clearTimeout(agentTimeoutRef.current)
      agentTimeoutRef.current = null
    }
    setIsWaitingForAgent(false)

    const currentRoom = roomRef.current
    if (currentRoom) {
      // Mark as intentional FIRST - prevents reconnection attempts
      intentionalDisconnectRef.current = true

      // Send end_session message to agent before disconnecting
      try {
        const encoder = new TextEncoder()
        await currentRoom.localParticipant.publishData(
          encoder.encode('end_session'),
          { reliable: true }
        )
        // Brief delay to ensure message is sent
        await new Promise(resolve => setTimeout(resolve, 100))
      } catch (e) {
        console.warn('Failed to send end_session message:', e)
      }

      // Remove all event listeners BEFORE disconnecting to prevent
      // reconnection handlers from firing
      currentRoom.removeAllListeners()

      roomRef.current = null
      // Pass true to stop local tracks immediately
      await currentRoom.disconnect(true)
      setRoom(null)
      setConnectionState(ConnectionState.Disconnected)
      setIsMuted(false)

      // Clean up audio element
      if (audioElementRef.current) {
        audioElementRef.current.remove()
        audioElementRef.current = null
      }
    }
  }, [])

  const toggleMute = useCallback(() => {
    const currentRoom = roomRef.current
    if (currentRoom?.localParticipant) {
      const newMuted = !isMuted
      currentRoom.localParticipant.setMicrophoneEnabled(!newMuted)
      setIsMuted(newMuted)
    }
  }, [isMuted])

  const retryAgentConnection = useCallback(async () => {
    console.log('Retrying agent connection...')
    setAgentTimedOut(false)
    setError(null)
    // Disconnect and reconnect will trigger fresh agent dispatch
    await disconnect()
    // Brief delay to ensure clean state
    await new Promise(resolve => setTimeout(resolve, 100))
    await connect(lastConnectOptionsRef.current)
  }, [disconnect, connect])

  // StrictMode-safe cleanup
  useEffect(() => {
    isMountedRef.current = true
    intentionalDisconnectRef.current = false
    // Reset room ref if it exists but is disconnected (stale state from previous mount)
    if (roomRef.current && roomRef.current.state === ConnectionState.Disconnected) {
      roomRef.current = null
    }

    return () => {
      isMountedRef.current = false
      // Capture ref values for cleanup
      const roomToCleanup = roomRef.current
      const audioToCleanup = audioElementRef.current
      const timeoutToCleanup = agentTimeoutRef.current

      // Clear agent timeout immediately
      if (timeoutToCleanup) {
        clearTimeout(timeoutToCleanup)
        agentTimeoutRef.current = null
      }

      // Use setTimeout(0) to let StrictMode re-mount happen first
      // If component truly unmounts, isMountedRef stays false
      setTimeout(() => {
        if (!isMountedRef.current && roomToCleanup) {
          console.log('Cleanup: disconnecting room')
          // Mark as intentional before cleanup disconnect
          intentionalDisconnectRef.current = true
          roomToCleanup.removeAllListeners()
          roomToCleanup.disconnect(true)
          roomRef.current = null
        }
        if (!isMountedRef.current && audioToCleanup) {
          audioToCleanup.remove()
          audioElementRef.current = null
        }
      }, 0)
    }
  }, [])

  // Browser offline/online detection for immediate feedback
  useEffect(() => {
    const handleOffline = () => {
      console.log('Browser went offline')
      if (isMountedRef.current && roomRef.current) {
        setNetworkState((prev) => ({ ...prev, isReconnecting: true }))
      }
    }

    const handleOnline = () => {
      console.log('Browser came back online')
      // LiveKit will handle actual reconnection, but we can log it
    }

    window.addEventListener('offline', handleOffline)
    window.addEventListener('online', handleOnline)

    return () => {
      window.removeEventListener('offline', handleOffline)
      window.removeEventListener('online', handleOnline)
    }
  }, [])

  return {
    room,
    sessionId,
    connectionState,
    isConnecting,
    isConnected: connectionState === ConnectionState.Connected,
    isWaitingForAgent,
    agentTimedOut,
    error,
    isMuted,
    networkState: {
      ...networkState,
      isAgentConnected,
    },
    connect,
    disconnect,
    toggleMute,
    retryAgentConnection,
  }
}
